import { Canvas } from '@react-three/fiber'
import { memo, Suspense, useEffect, useRef } from 'react'
import type { GameRuntime } from '../game/runtime'
import type { CameraMode } from './FollowCamera'
import { RaceScene } from './RaceScene'
import { createWebGLDetector } from './scene-contract'

interface GameCanvasProps {
  runtime: GameRuntime
  cameraMode?: CameraMode
  reducedMotion?: boolean
  manualSimulation?: boolean
  onPrepared: () => void
  onPreparationError: (error: Error) => void
}

export const supportsWebGL = createWebGLDetector(
  () => document.createElement('canvas'),
  () => navigator.userAgent,
)

const CAMERA = { position: [0, 7, -12] as [number, number, number], fov: 60, near: 0.1, far: 620 }
const GL_OPTIONS = { antialias: false, alpha: false, stencil: false, powerPreference: 'high-performance' as const }

function WebGLFallback({ onPreparationError }: Pick<GameCanvasProps, 'onPreparationError'>) {
  const reported = useRef(false)
  useEffect(() => {
    if (reported.current) return
    reported.current = true
    onPreparationError(new Error('WebGL is unavailable. Scene preparation cannot continue.'))
  }, [onPreparationError])

  return (
    <div role="status">
      <p>TRIDE requires WebGL to render the race.</p>
    </div>
  )
}

export const GameCanvas = memo(function GameCanvas({
  runtime,
  cameraMode = 'chase',
  reducedMotion = false,
  manualSimulation = false,
  onPrepared,
  onPreparationError,
}: GameCanvasProps) {
  if (!supportsWebGL()) {
    return <WebGLFallback onPreparationError={onPreparationError} />
  }
  return (
    <Canvas
      shadows="percentage"
      dpr={[1, 1.5]}
      camera={CAMERA}
      gl={GL_OPTIONS}
      frameloop={manualSimulation ? 'demand' : 'always'}
    >
      <Suspense fallback={null}>
        <RaceScene
          runtime={runtime}
          cameraMode={cameraMode}
          reducedMotion={reducedMotion}
          manualSimulation={manualSimulation}
          onPrepared={onPrepared}
          onPreparationError={onPreparationError}
        />
      </Suspense>
    </Canvas>
  )
})
